import { motion } from 'framer-motion'
import { Sparkles } from 'lucide-react'
import type { BdrLead, Cadencia } from '@/types'
import { getBdrInitials } from '@/components/BdrPanelV2/bdrTableShared'

export interface BdrQueueLeadCardProps {
  lead: BdrLead
  cad: Cadencia | null
  horario?: { label: string; color: string }
  showTentativa?: boolean
  showHorario?: boolean
  showFollowUp?: boolean
  showAgendado?: boolean
  showAmanha?: boolean
  actions?: React.ReactNode
  onOpen: () => void
}

export function BdrQueueLeadCard({
  lead,
  cad,
  horario,
  showTentativa,
  showHorario,
  showFollowUp,
  showAgendado,
  showAmanha,
  actions,
  onOpen,
}: BdrQueueLeadCardProps) {
  const tentativa = cad?.tentativa ?? 0

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.15 }}
      onClick={onOpen}
      className="relative flex flex-col h-full glass-card border-white/[0.06] hover:border-accent/40 bg-white/[0.01] hover:bg-white/[0.03] p-4 cursor-pointer transition-all duration-200 group rounded-xl overflow-hidden shadow-sm hover:shadow-accent/5"
    >
      <div className="flex items-center justify-between mb-3 min-h-[22px]">
        <div className="flex flex-wrap gap-1.5">
          {showTentativa && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-white/[0.04] text-text-muted border border-white/[0.05] tracking-wide">
              T {tentativa}/15
            </span>
          )}
          {showHorario && horario && (
            <span
              className={`text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-white/[0.04] ${horario.color} border border-current/20 tracking-wide`}
            >
              {horario.label}
            </span>
          )}
          {showFollowUp && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 uppercase tracking-wider">
              Follow-up
            </span>
          )}
          {showAgendado && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md text-amber-400 bg-amber-500/10 border border-amber-500/20 uppercase tracking-wider">
              Agendado
            </span>
          )}
          {showAmanha && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md text-purple-400 bg-purple-500/10 border border-purple-500/20 uppercase tracking-wider">
              Amanhã
            </span>
          )}
        </div>

        {cad?.proximaAcao && (
          <span className="text-[9px] font-semibold text-text-muted bg-black/20 px-2 py-0.5 rounded-md border border-white/[0.04]">
            {cad.proximaAcao.split('T')[0]}
          </span>
        )}
      </div>

      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center text-accent font-black text-sm shrink-0 border border-accent/10 shadow-inner mt-0.5">
          {getBdrInitials(lead.empresa)}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-text-primary text-sm font-bold truncate group-hover:text-accent-light transition-colors">
            {lead.empresa}
          </h4>
          <p className="text-text-muted text-xs truncate mt-0.5">{lead.contato}</p>
          {lead.cargo && (
            <p className="text-[10px] text-text-muted/60 truncate mt-0.5 uppercase tracking-wide font-medium">
              {lead.cargo}
            </p>
          )}
        </div>
      </div>

      <div className="mt-auto pt-3 border-t border-white/[0.04] flex items-center justify-between">
        <span className="text-[10px] font-bold text-text-muted uppercase tracking-wider flex items-center gap-1.5">
          <Sparkles
            size={11}
            className={lead.score && lead.score > 50 ? 'text-amber-400' : 'text-accent/70'}
          />
          {lead.score ? `${lead.score} Score` : 'S/ Score'}
        </span>

        {actions && (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {actions}
          </div>
        )}
      </div>
    </motion.div>
  )
}

export function BdrQueueActionBtn({
  icon: Icon,
  color,
  title,
  onClick,
}: {
  icon: React.ElementType
  color: string
  title: string
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/[0.1] transition-all hover:scale-110 active:scale-95"
    >
      <Icon size={14} style={{ color }} />
    </button>
  )
}
